import { useState } from "react";
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { submitContactMessage } from "@/lib/api";

const Contact = () => {
    const [form, setForm] = useState({
        name: "",
        email: "",
        subject: "",
        message: "",
    });
    const [loading, setLoading] = useState(false);

    const onChange = (e) =>
        setForm({ ...form, [e.target.name]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.subject || !form.message) {
            toast.error("Please fill out every field.");
            return;
        }
        setLoading(true);
        try {
            await submitContactMessage(form);
            toast.success("Message sent. We'll be in touch shortly.");
            setForm({ name: "", email: "", subject: "", message: "" });
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Something went wrong. Try again."
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="contact-page" className="relative">
            <section className="relative pt-40 pb-12 md:pt-48 md:pb-16">
                <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
                <div className="relative mx-auto max-w-5xl px-5 md:px-8">
                    <p className="mono text-xs text-brand-green uppercase mb-5">
                        / Contact
                    </p>
                    <h1 className="display text-5xl md:text-7xl text-[#1a1a1a]">
                        Let's get<br />
                        <span className="text-brand-green glow-text">
                            you moving.
                        </span>
                    </h1>
                </div>
            </section>

            <section className="relative py-16">
                <div className="mx-auto max-w-7xl px-5 md:px-8 grid md:grid-cols-12 gap-10">
                    <div className="md:col-span-5 space-y-6">
                        <div className="glass rounded-2xl p-7">
                            <div className="h-11 w-11 rounded-lg bg-brand-green/10 flex items-center justify-center mb-5">
                                <Mail size={18} className="text-brand-green" />
                            </div>
                            <p className="mono text-xs text-gray-500 uppercase mb-2">
                                Email
                            </p>
                            <a
                                href="mailto:info@nexship.co.za"
                                className="text-[#1a1a1a] hover:text-brand-green text-lg"
                                data-testid="contact-email"
                            >
                                info@nexship.co.za
                            </a>
                        </div>
                        <div className="glass rounded-2xl p-7">
                            <div className="h-11 w-11 rounded-lg bg-brand-blue/10 flex items-center justify-center mb-5">
                                <Phone size={18} className="text-brand-blue" />
                            </div>
                            <p className="mono text-xs text-gray-500 uppercase mb-2">
                                Phone
                            </p>
                            <p className="text-[#1a1a1a] text-lg">
                                +27 (0) 71 871 7111
                            </p>
                        </div>
                        <div className="glass rounded-2xl p-7">
                            <div className="h-11 w-11 rounded-lg bg-brand-green/10 flex items-center justify-center mb-5">
                                <MapPin size={18} className="text-brand-green" />
                            </div>
                            <p className="mono text-xs text-gray-500 uppercase mb-2">
                                Office
                            </p>
                            <p className="text-[#1a1a1a] text-lg">
                                Gauteng, South Africa
                            </p>
                        </div>
                    </div>

                    <form
                        onSubmit={onSubmit}
                        className="md:col-span-7 brand-border-soft rounded-2xl p-8 md:p-10"
                        data-testid="contact-form"
                    >
                        <p className="mono text-xs text-brand-green uppercase mb-6">
                            / Send us a message
                        </p>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field
                                label="Name"
                                name="name"
                                value={form.name}
                                onChange={onChange}
                                testid="contact-name"
                            />
                            <Field
                                label="Email"
                                type="email"
                                name="email"
                                value={form.email}
                                onChange={onChange}
                                testid="contact-email-input"
                            />
                        </div>
                        <div className="mt-4">
                            <Field
                                label="Subject"
                                name="subject"
                                value={form.subject}
                                onChange={onChange}
                                testid="contact-subject"
                            />
                        </div>
                        <div className="mt-4">
                            <Field
                                label="Message"
                                name="message"
                                value={form.message}
                                onChange={onChange}
                                textarea
                                testid="contact-message"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="contact-submit"
                            className="btn-primary mt-8 w-full sm:w-auto justify-center disabled:opacity-60"
                        >
                            {loading ? (
                                <Loader2 className="animate-spin" size={18} />
                            ) : (
                                <Send size={16} />
                            )}
                            {loading ? "Sending..." : "Send Message"}
                        </button>
                    </form>
                </div>
            </section>
        </div>
    );
};

export const Field = ({
    label,
    name,
    value,
    onChange,
    type = "text",
    textarea = false,
    testid,
    placeholder = "",
}) => (
    <label className="block">
        <span className="mono text-xs text-gray-500 uppercase tracking-wider mb-2 block">
            {label}
        </span>
        {textarea ? (
            <textarea
                name={name}
                value={value}
                onChange={onChange}
                rows={5}
                data-testid={testid}
                placeholder={placeholder}
                className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-[#1a1a1a] placeholder-gray-400 focus:outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40 transition-colors"
            />
        ) : (
            <input
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                data-testid={testid}
                placeholder={placeholder}
                className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-[#1a1a1a] placeholder-gray-400 focus:outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40 transition-colors"
            />
        )}
    </label>
);

export default Contact;
